import type { CartItem } from "../stores/cart";
import { SUPPLEMENTS_EXPERIMENT_KEY, type SupplementProduct } from "./api";

export const SUPPLEMENT_ANALYTICS_CONTEXT = {
  experiment_key: SUPPLEMENTS_EXPERIMENT_KEY,
  variant_key: "variant-a",
  source: "shop_supplements_tab",
};

/** Analytics uses the exact listing identity without referral/query parameters. */
export function canonicalSupplementAmazonUrl(asin?: string | null) {
  return asin && /^[A-Z0-9]{10}$/.test(asin)
    ? `https://www.amazon.com/dp/${asin}`
    : null;
}

export function supplementProductProperties(product: SupplementProduct) {
  return {
    product_id: product.id,
    product_name: product.title,
    product_category: product.category,
    // Preserve the property used by the original product-view event.
    category: product.category,
    brand: product.brand,
    asin: product.asin,
    amazon_url: canonicalSupplementAmazonUrl(product.asin),
    unit_price_cents: product.priceCents,
    currency: product.currency,
  };
}

export function supplementCartProperties(
  items: CartItem[],
  catalogProducts: SupplementProduct[] = [],
) {
  const products = items
    .filter((item) => item.origin === "supplement")
    .map((item) => {
      // Older carts predate analytics metadata. Only the same product identity
      // can fill missing fields; this never changes payment or fulfillment.
      const product = catalogProducts.find(
        (candidate) =>
          candidate.id === item.id &&
          (!item.supplement_asin || candidate.asin === item.supplement_asin),
      );
      const asin = item.supplement_asin ?? product?.asin ?? null;
      const category = item.supplement_category ?? product?.category ?? null;
      return {
        product_id: item.id,
        product_name: item.title,
        product_category: category,
        category,
        brand: item.supplement_brand ?? product?.brand ?? null,
        asin,
        amazon_url: canonicalSupplementAmazonUrl(asin),
        unit_price_cents: item.price_cents,
        quantity: item.quantity,
        amount_cents: item.price_cents * item.quantity,
        currency: "USD",
      };
    });
  return {
    products,
    product_ids: products.map((product) => product.product_id),
    item_count: products.length,
    quantity: products.reduce((sum, product) => sum + product.quantity, 0),
    amount_cents: products.reduce(
      (sum, product) => sum + product.amount_cents,
      0,
    ),
    currency: "USD",
  };
}
